import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";
import FilterBar, { type FilterValue } from "./components/FilterBar";
import Pagination from "./components/Pagination";
import { filterDramas } from "@/utils/content";

const PAGE_SIZE = 8;

const DEFAULTS: FilterValue = { type: "", region: "", year: "", sort: "new" };

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterValue>(() => ({
    type: searchParams.get("type") ?? "",
    region: searchParams.get("region") ?? "",
    year: searchParams.get("year") ?? "",
    sort: searchParams.get("sort") ?? "new",
  }));
  const [page, setPage] = useState(() => Number(searchParams.get("page") ?? 1));

  useEffect(() => {
    document.title = "影视分类 - 隔壁的女孩";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "影视分类页汇集最新电影、电视剧与网剧，可按类型、地区、年份筛选并排序，快速找到想看的影视剧。",
      );
    }
  }, []);

  const results = useMemo(
    () =>
      filterDramas({
        type: filters.type,
        region: filters.region,
        year: filters.year,
        sort: filters.sort,
      }),
    [filters],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const syncParams = (next: FilterValue, nextPage: number) => {
    const params: Record<string, string> = {};
    if (next.type) params.type = next.type;
    if (next.region) params.region = next.region;
    if (next.year) params.year = next.year;
    if (next.sort && next.sort !== "new") params.sort = next.sort;
    if (nextPage > 1) params.page = String(nextPage);
    setSearchParams(params, { replace: true });
  };

  const handleFilterChange = (patch: Partial<FilterValue>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setPage(1);
    syncParams(next, 1);
  };

  const handleReset = () => {
    setFilters(DEFAULTS);
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    syncParams(filters, nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="pt-16 md:pt-[72px]">
        <div className="border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类" }]} />
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              影视剧分类及筛选
            </h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600">
              按类型、地区、年份筛选你想看的影视内容，也可以切换排序方式，快速定位最新或最热门的作品。
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
          <FilterBar value={filters} onChange={handleFilterChange} onReset={handleReset} />

          <div className="mt-5 flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-medium text-accent-600">{results.length}</span> 部影视内容
            </p>
          </div>

          {pageItems.length > 0 ? (
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {pageItems.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 px-4 text-center">
              <span className="flex items-center justify-center w-14 h-14 rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-inbox-2-line text-3xl"></i>
              </span>
              <h3 className="font-heading text-lg font-semibold text-foreground-900">没有找到匹配的影视内容</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-500">
                当前的筛选条件组合暂时没有结果，试试放宽条件，或点击下方按钮查看全部影视。
              </p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          )}

          <Pagination page={currentPage} totalPages={totalPages} onChange={handlePageChange} />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}